/**
 * What one submission covers.
 *
 * Timesheets are submitted by the week but evaluated by the month: payroll and the
 * exec reports are pulled at month end. A week that crosses a month end could only
 * be submitted whole, so finalising September meant submitting - and locking - the
 * October days of that week before anybody had worked them. People either guessed
 * those days in advance or left September open.
 *
 * So a week that straddles a month change is submitted in TWO parts, split where
 * the month changes, and each part is submitted, approved, sent back and reopened
 * on its own. A week inside one month is one part, exactly as before.
 *
 * This is the same line the lockdown boundary already draws - clampBoundary cuts a
 * straddling week at the end of the month it started in - so the boundary and the
 * submission now agree about where a week can be divided.
 */
import { addDays, endOfMonth, endOfWeek, sameMonth, startOfWeek, type DateOnly } from './time';

export interface SubmissionPeriod {
  start: DateOnly;
  end: DateOnly;
}

export type PeriodStatus = 'NOT_SUBMITTED' | 'SUBMITTED' | 'APPROVED' | 'DENIED';

/** The one or two parts of the ISO week containing `day`, earliest first. */
export function submissionPeriods(day: DateOnly): SubmissionPeriod[] {
  const monday = startOfWeek(day);
  const sunday = endOfWeek(day);
  if (sameMonth(monday, sunday)) return [{ start: monday, end: sunday }];
  const monthEnd = endOfMonth(monday);
  return [
    { start: monday, end: monthEnd },
    { start: addDays(monthEnd, 1), end: sunday },
  ];
}

/** The part of its week that `day` belongs to. */
export function submissionPeriodOf(day: DateOnly): SubmissionPeriod {
  return submissionPeriods(day).find((p) => day >= p.start && day <= p.end)!;
}

/** True when this period is only part of its week. */
export function isPartialWeek(period: SubmissionPeriod): boolean {
  return period.start !== startOfWeek(period.start) || period.end !== endOfWeek(period.start);
}

/**
 * The submitted or approved period covering `day`, if any.
 *
 * Reads the STORED bounds rather than recomputing them from the day. Weeks
 * submitted before the split existed are stored whole, including straddling ones,
 * and a whole submitted week has to keep locking all seven of its days - what a
 * supervisor signed off must not reopen because the rule for new weeks changed.
 */
export function closedPeriodOn<P extends SubmissionPeriod & { status: PeriodStatus }>(
  day: DateOnly,
  periods: readonly P[],
): P | null {
  return (
    periods.find(
      (p) => (p.status === 'SUBMITTED' || p.status === 'APPROVED') && day >= p.start && day <= p.end,
    ) ?? null
  );
}

/**
 * The parts a week is SHOWN and counted as, given what is stored for it.
 *
 * Normally the split of submissionPeriods. The exception is a whole straddling
 * week submitted before the split existed: while that row is submitted or
 * approved it is one part covering all seven days, because that is what somebody
 * sent and somebody signed off. Once it is sent back or reopened it is open again,
 * and an open week is submitted the way every week is now - in two parts - so it
 * is shown that way. Showing it whole would offer "Submit week" for a submit that
 * only sends one side of the month.
 *
 * The week screens, the board and the reminder all call this, so they cannot
 * disagree about how many parts a week has.
 */
export function partsOfWeek(
  weekStart: DateOnly,
  stored: readonly (SubmissionPeriod & { status: PeriodStatus })[],
): SubmissionPeriod[] {
  const monday = startOfWeek(weekStart);
  const sunday = endOfWeek(weekStart);
  const whole = stored.find(
    (row) =>
      row.start === monday &&
      row.end === sunday &&
      (row.status === 'SUBMITTED' || row.status === 'APPROVED'),
  );
  return whole ? [{ start: monday, end: sunday }] : submissionPeriods(monday);
}

/** True when every day from `from` to `to` is covered by a submitted or approved part. */
export function allClosed(
  from: DateOnly,
  to: DateOnly,
  stored: readonly (SubmissionPeriod & { status: PeriodStatus })[],
): boolean {
  for (let day = from; day <= to; day = addDays(day, 1)) {
    if (closedPeriodOn(day, stored) === null) return false;
  }
  return true;
}

const MONTH = new Intl.DateTimeFormat('en-IN', { month: 'long', timeZone: 'UTC' });
const DAY_MONTH = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** "September", for the part of a week that falls in September. */
export function periodMonthName(period: SubmissionPeriod): string {
  return MONTH.format(new Date(`${period.start}T00:00:00.000Z`));
}

/** "28 Sept – 30 Sept", or the single day when a part is one day long. */
export function formatPeriod(period: SubmissionPeriod): string {
  const from = DAY_MONTH.format(new Date(`${period.start}T00:00:00.000Z`));
  if (period.start === period.end) return from;
  return `${from} – ${DAY_MONTH.format(new Date(`${period.end}T00:00:00.000Z`))}`;
}

/**
 * Why a write to a submitted day was refused.
 *
 * Names the part when the week is split. "That week is waiting for approval" on a
 * Thursday nobody submitted reads as a bug; "submitted with September's part"
 * says which half is shut and why the other one is not.
 */
export function submittedReason(
  period: SubmissionPeriod & { status: 'SUBMITTED' | 'APPROVED' },
): string {
  const what = isPartialWeek(period)
    ? `${periodMonthName(period)}'s part of this week (${formatPeriod(period)})`
    : 'That week';
  return period.status === 'APPROVED'
    ? `${what} has been approved and can no longer be changed. Ask your supervisor to reopen it.`
    : `${what} is waiting for approval. Ask your supervisor to send it back if it needs correcting.`;
}
